import { AsyncPipe, DatePipe, NgClass, NgTemplateOutlet } from "@angular/common";
import {
    Component,
    TemplateRef,
    contentChild,
    input,
    signal,
    ChangeDetectionStrategy
} from "@angular/core";
import { RouterLink } from "@angular/router";
import { Observable } from "rxjs";
import { BusySpinnerComponent } from "~app/common/components/busy/busy-spinner/busy-spinner.component";
import { MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { AlertListSortComponent, AlertListSortSettings } from "./alert-list-sort/alert-list-sort.component";

export type AlertItem = {
    title: string;
    message: string;
    description?: string;
    timestamp?: Date;
    severity: MessageSeverity;
    cssClass?: string;
    links?: AlertItemLink[];
    metadata?: { [key: string]: any };
};

export type AlertItemLink = {
    text: string;
    resource: string;
    queryParams?: { [key: string]: string };
    isExternal?: boolean;
    title?: string;
};

const sortAlertItems = (items: AlertItem[], settings: AlertListSortSettings): AlertItem[] => {
    const compareText = (a?: string, b?: string): number =>
        (a ?? "").localeCompare(b ?? "", undefined, { sensitivity: "base" });

    const compareSeverity = (a: AlertItem, b: AlertItem): number => a.severity - b.severity;

    return [...items].sort((a, b) => {
        let primaryComparison = 0;

        switch (settings.method) {
            case "title":
                primaryComparison = compareText(a.title, b.title);
                break;
            case "message":
                primaryComparison = compareText(a.message, b.message);
                break;
            case "severity":
                primaryComparison = compareSeverity(a, b);
                break;
        }

        const initialComparison = settings.direction === "asc"
            ? primaryComparison
            : primaryComparison * -1;

        if (initialComparison !== 0) {
            return initialComparison;
        }

        return (b.timestamp?.getTime() ?? 0) - (a.timestamp?.getTime() ?? 0);
    });
};

@Component({
    selector: "rask-alert-list",
    imports: [
        AsyncPipe,
        DatePipe,
        NgClass,
        NgTemplateOutlet,
        RouterLink,
        AlertListSortComponent,
        BusySpinnerComponent
    ],
    templateUrl: "./alert-list.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: "./alert-list.component.scss"
})
export class AlertListComponent {
    protected readonly title = input<string>();
    protected readonly data = input<Observable<AlertItem[] | null>>();
    protected readonly iconsTemplate = contentChild<TemplateRef<{ $implicit: AlertItem }>>("icons", { descendants: true });
    protected readonly sortSettings = signal<AlertListSortSettings>({
        method: "title",
        direction: "asc"
    });

    protected getSortedData(items: AlertItem[]): AlertItem[] {
        if (!items) {
            return [];
        }

        return sortAlertItems(items, this.sortSettings());
    }

    protected onSortChanged(settings: AlertListSortSettings): void {
        this.sortSettings.set(settings);
    }
}
